import React, {
  useContext,
  useEffect,
  useState,
  ReactEventHandler,
  ChangeEvent,
  FormEvent,
  useMemo,
} from "react";
import NavigationPrompt from "react-router-navigation-prompt";
import { Tab, Tabs, TabList, TabPanel } from "react-tabs";

// @ts-ignore
import { store } from "react-notifications-component";

import GlobalData from "../../context/globaldata";

import { Header } from "../../components/Layout";
import {
  Input,
  Checkbox,
  Select,
  AccentuateBox,
  Textarea,
} from "../../components/Form";
import Button from "../../components/Button/Button";
import MapsAutocomplete from "../../components/Maps/Autocomplete";
import Modal from "../../components/Modal/Modal";

import { ReactComponent as IconCrossRed } from "../../assets/images/icons/cross-small-red.svg";
import { ReactComponent as IconPlusSmall } from "../../assets/images/icons/plus-small.svg";
import { nanoid } from "nanoid";

import subRegionOptionsData from "../../assets/data/sub-region-options.json";
import countryCodes from "../../assets/data/country-codes-iso3316-2.json";
import { useHistory } from "react-router-dom";

const subRegionOptions: StrictDict = subRegionOptionsData;
type CountryCode = keyof typeof countryCodes;

export default function SupplierSettings(props: any) {
  const context = useContext(GlobalData);
  const history = useHistory();
  const uid = props.supplierUid;
  const supplier = props.supplier;
  // Check if GENLAND is an array or string
  const supplierCarCountry =
    supplier.car?.CompanyAddress.GENLAND instanceof Array
      ? supplier.car?.CompanyAddress.GENLAND[0]
      : supplier.car?.CompanyAddress.GENLAND;

  const setSupplier = (data: Supplier | Function) => {
    setNotSaved(true);
    props.setSupplier(data);
  };

  const [deleteModalVisible, setDeleteModalVisible] = useState<boolean>(false);

  const [newLane, setNewLane] = useState<StrictDict>({
    origin: null,
    destination: null,
  });

  const [supplierCopy, setSupplierCopy] = useState<Supplier>();

  const [notSaved, setNotSaved] = useState<boolean>(false);
  const [loadingGlobal, setLoadingGlobal] = useState<boolean>(false);

  const setSettingsVisible = props.setSettingsVisible;

  useEffect(() => {
    setSettingsVisible(true);

    return () => {
      setSettingsVisible(false);
    };
  }, [setSettingsVisible]);

  useEffect(() => {
    // Save a version of the current edited item for
    if (supplier.relationship !== undefined && !supplierCopy) {
      setSupplierCopy(JSON.parse(JSON.stringify(supplier)));
    }
  }, [supplier, supplierCopy, setSupplierCopy]);

  const saveSettings = (event: Event) => {
    event?.preventDefault();

    const saveObject = {
      alias: supplier.alias || "",
      subtitle: supplier.subtitle || null,
      agreementSigned: supplier.agreementSigned || false,
      relationship: supplier.relationship || null,
      status: supplier.status || {},
      locationSubRegions: supplier.locationSubRegions || [],
      coverage: supplier.coverage || [],
      coverageLanes: supplier.coverageLanes || [],
      groups: supplier.groups || {},
      type: supplier.type || {},
      comment: supplier.comment || "",
      customName: supplier.customName || null,
      customPhone: supplier.customPhone || null,
      customEmail: supplier.customEmail || null,
      escalationName: supplier.escalationName || null,
      escalationEmail: supplier.escalationEmail || null,
      escalationPhone: supplier.escalationPhone || null,
      contacts: supplier.contacts || [],
      ignoreSupplierLocation: supplier.ignoreSupplierLocation || null,
      rating: supplier.rating || null,
      car: supplier.car || {},
    };

    process.env.NODE_ENV === "development" && console.log(saveObject);

    context.firestore
      .collection("suppliers")
      .doc(uid)
      .update(saveObject)
      .then(() => {
        store.addNotification({
          message: "Settings successfully updated",
          type: "success",
          insert: "top",
          container: "top-center",
          animationIn: ["animated", "slideDown"],
          animationOut: ["animated", "slideUp"],
          dismiss: {
            pauseOnHover: true,
            duration: 5000,
          },
        });

        setNotSaved(false);
      })
      .catch((error: string) => {
        store.addNotification({
          message: `Error updating document: ${error}`,
          type: "danger",
          insert: "top",
          container: "top-center",
          animationIn: ["animated", "slideDown"],
          animationOut: ["animated", "slideUp"],
          dismiss: {
            pauseOnHover: true,
            duration: 5000,
          },
        });
      })
      .finally(() => {});
  };

  const updateSupplier = (event: ChangeEvent<HTMLInputElement>) => {
    switch (event.target.name) {
      case "car.CompanyAddress.GENLAND":
        break;
    }

    setSupplier({
      ...supplier,
      [event.target.name]: event.target.value,
    });
  };

  const updateType = (event: ChangeEvent<HTMLInputElement>) => {
    setSupplier({
      ...supplier,
      type: {
        ...supplier.type,
        [event.target.value]: supplier.type
          ? !supplier.type[event.target.value]
          : false,
      },
    });
  };

  const setRelationship = (newValue: any) => {
    setSupplier({
      ...supplier,
      relationship: newValue.value,
    });
  };

  const setStatusInactive = (newValue: boolean) => {
    setSupplier({
      ...supplier,
      status: {
        ...supplier.status,
        inactive: newValue,
      },
    });
  };

  const getRatingRestToPercentage = useMemo(() => {
    // Get rest of rating value and convert to percentage
    process.env.NODE_ENV === "development" &&
      console.log((supplier.rating % 1) * 100);
    return (supplier.rating % 1) * 100;
  }, [supplier.rating]);

  const setActiveGroup = (event: ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;

    setSupplier({
      ...supplier,
      groups: {
        ...supplier.groups,
        [event.target.value]: isChecked,
      },
    });
  };

  const onCoverageAdd = (locationItem: MapLocation) => {
    if (!locationItem) {
      return;
    }

    if (
      supplier.coverage?.some(
        (selectedCountry: StrictDict) =>
          selectedCountry.item.country === locationItem.country
      )
    ) {
      return;
    }

    const supplierCoverage = supplier.coverage ? [...supplier.coverage] : [];

    supplierCoverage.push({
      id: "",
      item: locationItem,
      selected: false,
    });

    setSupplier({
      ...supplier,
      coverage: supplierCoverage,
    });
  };

  const removeCountry = (index: number) => {
    const coverageCopy = supplier.coverage.slice();

    coverageCopy.splice(index, 1);

    setSupplier({
      ...supplier,
      coverage: coverageCopy,
    });
  };

  const allCountryNames = useMemo(
    () => Object.values(countryCodes).map((c: any) => c.name),
    []
  );

  const isGlobal = useMemo(() => {
    if (!supplier.coverage || supplier.coverage.length === 0) return false;
    const coveredCountries = new Set(
      supplier.coverage.map((c: any) => c.item.country)
    );
    return allCountryNames.every((name) => coveredCountries.has(name));
  }, [supplier.coverage, allCountryNames]);

  const handleGlobalToggle = () => {
    if (isGlobal) {
      setSupplier({ ...supplier, coverage: [] });
    } else {
      setLoadingGlobal(true);
      setTimeout(() => {
        const allCoverage = Object.values(countryCodes).map((country: any) => ({
          id: "",
          item: {
            id: "",
            name: country.name,
            country: country.name,
            continent: context.countriesContinents[country.name] || "",
            lat: 0,
            long: 0,
          },
        }));
        setSupplier({ ...supplier, coverage: allCoverage });
        setLoadingGlobal(false);
      }, 50);
    }
  };

  const updateCustomEmail = (event: any) => {
    setSupplier({
      ...supplier,
      customEmail: event.target.value,
    });
  };

  const updateCustomName = (event: any) => {
    setSupplier({
      ...supplier,
      customName: event.target.value,
    });
  };

  const updateEscalationName = (event: any) => {
    setSupplier({
      ...supplier,
      escalationName: event.target.value,
    });
  };

  const updateCustomPhone = (event: any) => {
    setSupplier({
      ...supplier,
      customPhone: event.target.value,
    });
  };

  const updateEscalationEmail = (event: any) => {
    setSupplier({
      ...supplier,
      escalationEmail: event.target.value,
    });
  };

  const updateEscalationPhone = (event: any) => {
    setSupplier({
      ...supplier,
      escalationPhone: event.target.value,
    });
  };

  const updateContact = (
    event: ChangeEvent<HTMLInputElement>,
    contactIndex: number,
    field: "email" | "phone" | "name"
  ) => {
    const value = event.target.value;

    setSupplier((prevState: Supplier) => {
      const newState: Supplier = Object.assign({}, prevState);
      newState.contacts![contactIndex][field] = value;

      return newState;
    });
  };

  const addContact = () => {
    setSupplier((prevState: Supplier) => {
      const newState: Supplier = Object.assign({}, prevState);

      if (!newState.contacts) {
        newState.contacts = [];
      }

      newState.contacts.push({
        name: "",
        email: "",
        phone: "",
      });

      return newState;
    });
  };

  const removeContact = (contactIndex: number) => {
    setSupplier((prevState: Supplier) => {
      const newState: Supplier = Object.assign({}, prevState);

      newState.contacts?.splice(contactIndex, 1);

      return newState;
    });
  };

  const updateNewLane = (locationItem: MapLocation, direction: string) => {
    setNewLane({
      ...newLane,
      [direction]: locationItem,
    });
  };

  const addNewLane = (event: FormEvent) => {
    event.preventDefault();

    if (!newLane.origin || !newLane.destination) {
      return;
    }

    const supplierCoverageLanes = supplier.coverageLanes
      ? [...supplier.coverageLanes]
      : [];

    supplierCoverageLanes.push({
      id: nanoid(),
      lane: newLane,
    });

    setSupplier({
      ...supplier,
      coverageLanes: supplierCoverageLanes,
    });

    setNewLane({
      origin: null,
      destination: null,
    });
  };

  const removeLane = (index: number) => {
    const coverageLanesCopy = supplier.coverageLanes.slice();

    coverageLanesCopy.splice(index, 1);

    setSupplier({
      ...supplier,
      coverageLanes: coverageLanesCopy,
    });
  };

  if (!supplier.car) {
    return null;
  }

  return (
    <>
      <NavigationPrompt when={notSaved}>
        {({ onConfirm, onCancel }) => (
          <Modal
            visible={true}
            confirmAction={onCancel}
            cancelAction={() => {
              // Place the confirm on the cancel button
              if (supplierCopy) {
                setSupplier(supplierCopy);
              }
              onConfirm();
            }}
            title="You have unsaved changes"
            confirmLabel={"Cancel"}
            cancelLabel={"Confirm"}
            noCloseButton={true}
          >
            <p>
              Are you sure you want to leave this page and discard your changes?
            </p>
          </Modal>
        )}
      </NavigationPrompt>
      <Header
        title={`Supplier settings`}
        subTitle={supplier.alias ? supplier.alias : supplier.car?.Client}
        backLink={`/suppliers/${supplier.uid}`}
        rightContent={
          <>
            {context.currentUser.custom?.admin && (
              <Button variant="primary" onClick={saveSettings}>
                Save settings
              </Button>
            )}

            <Button
              variant="ghost-danger"
              onClick={() => setDeleteModalVisible(true)}
            >
              Delete supplier
            </Button>
          </>
        }
        variant="secondary"
      />
      <Modal
        variant="danger"
        title="Are you sure?"
        visible={deleteModalVisible}
        confirmAction={() => {
          context.firestore
            .collection("suppliers")
            .doc(uid)
            .delete()
            .then(() => {
              store.addNotification({
                message: `Successfully deleted supplier: ${uid}`,
                type: "success",
                insert: "top",
                container: "top-center",
                animationIn: ["animated", "slideDown"],
                animationOut: ["animated", "slideUp"],
                dismiss: {
                  pauseOnHover: true,
                  duration: 5000,
                },
              });

              history.push("/suppliers");
            })
            .catch((error: string) => {
              props.setDeleted(false);
              store.addNotification({
                message: `Error deleting supplier: ${error}`,
                type: "danger",
                insert: "top",
                container: "top-center",
                animationIn: ["animated", "slideDown"],
                animationOut: ["animated", "slideUp"],
                dismiss: {
                  pauseOnHover: true,
                  duration: 5000,
                },
              });
            })
            .finally(() => {
              setDeleteModalVisible(false);
            });
        }}
        confirmLabel="Delete supplier"
        cancelAction={() => setDeleteModalVisible(false)}
      >
        Deleting this supplier makes it no longer accessible.
      </Modal>
      <div className="detail supplier-details">
        <div className="container">
          <Tabs className="tabbed-pages">
            <TabList>
              <Tab>General</Tab>
              <Tab>Contact details</Tab>
              <Tab>Address</Tab>
              <Tab>Country coverage</Tab>
            </TabList>

            <div className="tabbed-pages__page">
              {/* General */}
              <TabPanel>
                <div className="detail__top">
                  <h3>General</h3>
                </div>

                <div className="form-group">
                  <Select
                    label="Relationship status"
                    value={props.getRelationship()}
                    name="demoSelect"
                    onChange={(value: ReactEventHandler) =>
                      setRelationship(value)
                    }
                    options={props.relationshipOptions}
                  />
                </div>

                <div className="form-group">
                  <Input
                    value={supplier.alias || ""}
                    name="alias"
                    onChange={updateSupplier}
                    label="Alias name"
                    placeholder="e.g. Packimpex"
                  />
                </div>

                <div className="form-group">
                  <Input
                    value={supplier.subtitle || ""}
                    name="subtitle"
                    onChange={updateSupplier}
                    label="Supplier subtitle"
                    placeholder="e.g. 15 m3 / 750kgs vans"
                  />
                </div>

                <div className="form-group">
                  <label className="form-group__label">Groups</label>
                  {context.groups &&
                    context.groups.map((option: any) => (
                      <Checkbox
                        key={option.id}
                        value={option.id}
                        name="groups"
                        onChange={setActiveGroup}
                        label={option.name}
                        checked={
                          (supplier.groups &&
                            supplier.groups[option?.id] === true) ||
                          false
                        }
                      />
                    ))}
                </div>

                <div className="form-group">
                  <label className="form-group__label">Type</label>
                  <Checkbox
                    value="private"
                    label="Private"
                    name="type"
                    checked={supplier.type && supplier.type["private"]}
                    onChange={updateType}
                  />
                  <Checkbox
                    value="rmc"
                    label="RMC"
                    name="type"
                    checked={supplier.type && supplier.type["rmc"]}
                    onChange={updateType}
                  />
                  <Checkbox
                    value="diplo"
                    label="Diplo"
                    name="type"
                    checked={supplier.type && supplier.type["diplo"]}
                    onChange={updateType}
                  />
                </div>

                <div className="form-group">
                  <label className="form-group__label">
                    Packimpex SLA &amp; Compliant status
                  </label>
                  <Checkbox
                    value="true"
                    label="Signed"
                    name="agreementSigned"
                    checked={supplier.agreementSigned || false}
                    onChange={() =>
                      setSupplier({
                        ...supplier,
                        agreementSigned: !supplier.agreementSigned,
                      })
                    }
                  />
                </div>

                <div className="form-group">
                  <label className="form-group__label">Rating</label>
                  <div className={`rating-input`}>
                    {[1, 2, 3, 4, 5].map((rating: number) => {
                      return (
                        <button
                          key={rating}
                          className={`rating-input__star`}
                          name="rating"
                          id={`rating-${rating}`}
                          onClick={(event) => {
                            if (supplier.rating === rating) {
                              setSupplier({ ...supplier, rating: null });
                            } else {
                              setSupplier({ ...supplier, rating: rating });
                            }
                          }}
                        >
                          <div
                            className={`rating-input__star-inner ${
                              rating <= parseInt(supplier.rating) + 1
                                ? "rating-input__star-inner--active"
                                : ""
                            }`}
                            style={
                              parseInt(supplier.rating) === rating - 1
                                ? {
                                    clipPath:
                                      getRatingRestToPercentage != 0
                                        ? `polygon(0% 0%, ${getRatingRestToPercentage}% 0, ${getRatingRestToPercentage}% 100%, 0 100%)`
                                        : "polygon(0% 0%, 0 0, 0 100%, 0 100%)",
                                  }
                                : {}
                            }
                          />
                        </button>
                      );
                    })}
                  </div>
                  <input
                    className="rating-input__field"
                    type="number"
                    step="0.1"
                    min={0}
                    max={5}
                    value={supplier.rating === null ? 0 : supplier.rating}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      let inputValue = parseFloat(event.target.value);
                      if (inputValue > 5) {
                        inputValue = 5;
                      }
                      setSupplier({ ...supplier, rating: inputValue });
                    }}
                  />
                </div>

                <div className="form-group">
                  <Textarea
                    value={supplier.comment}
                    label="Comment"
                    name="comment"
                    placeholder="e.g. certain specifics regarding this supplier"
                    onChange={updateSupplier}
                    rows={5}
                  />
                </div>

                <AccentuateBox
                  heading={`Supplier status: ${
                    !supplier.status?.inactive ? "Active" : "Inactive"
                  }`}
                  content="When a supplier is deactivated it is not shown in the supplier overview."
                >
                  <Checkbox
                    value={supplier.status?.inactive}
                    checked={!supplier.status?.inactive || false}
                    name="inactive"
                    onChange={() =>
                      setStatusInactive(!supplier.status?.inactive)
                    }
                    switch
                  />
                </AccentuateBox>
              </TabPanel>

              {/* Contact details */}
              <TabPanel>
                <div className="detail__top">
                  <h3>Contact details</h3>
                </div>

                <div className="form-group">
                  <h4 className="form-group__title">Primary contact</h4>
                  <Input
                    label="Full name"
                    value={supplier.customName ? supplier.customName : ""}
                    placeholder={supplier.car.ContactInfo.INFCONT}
                    name="custom-name"
                    onChange={updateCustomName}
                    type="text"
                  />
                </div>

                <div className="form-group">
                  <Input
                    label="E-mail address"
                    value={supplier.customEmail ? supplier.customEmail : ""}
                    placeholder={supplier.car?.ContactInfo.INFEM[0]}
                    name="custom-email"
                    onChange={updateCustomEmail}
                    type="email"
                  />
                </div>

                <div className="form-group">
                  <Input
                    label="Phone"
                    value={supplier.customPhone ? supplier.customPhone : ""}
                    placeholder={supplier.car?.ContactInfo.INFTEL}
                    name="custom-phone"
                    onChange={updateCustomPhone}
                    type="tel"
                  />
                </div>

                <div className="form-group">
                  <h4 className="form-group__title">Escalation contact</h4>
                  <Input
                    label="Full name"
                    value={
                      supplier.escalationName ? supplier.escalationName : ""
                    }
                    name="escalation-name"
                    onChange={updateEscalationName}
                    type="text"
                  />
                </div>

                <div className="form-group">
                  <Input
                    label="E-mail address"
                    value={
                      supplier.escalationEmail ? supplier.escalationEmail : ""
                    }
                    name="escalation-email"
                    onChange={updateEscalationEmail}
                    type="email"
                  />
                </div>

                <div className="form-group">
                  <Input
                    label="Phone number"
                    value={
                      supplier.escalationPhone ? supplier.escalationPhone : ""
                    }
                    name="escalation-phone"
                    onChange={updateEscalationPhone}
                    type="tel"
                  />
                </div>

                {supplier.contacts ? (
                  supplier.contacts.map(
                    (contact: SupplierContact, contactIndex: number) => (
                      <React.Fragment key={contactIndex}>
                        <div className="form-group">
                          <Button
                            variant="ghost"
                            tip="Remove contact"
                            size="small"
                            circle
                            onClick={() => removeContact(contactIndex)}
                          >
                            <IconCrossRed />
                          </Button>
                          <h4 className="form-group__title">
                            Additional contact ({contactIndex + 1})
                          </h4>
                          <Input
                            label="Full name"
                            value={contact.name ? contact.name : ""}
                            name="contact-name"
                            onChange={(event: ChangeEvent<HTMLInputElement>) =>
                              updateContact(event, contactIndex, "name")
                            }
                            type="text"
                          />
                        </div>
                        <div className="form-group">
                          <Input
                            label="E-mail address"
                            value={contact.email ? contact.email : ""}
                            name="contact-email"
                            onChange={(event: ChangeEvent<HTMLInputElement>) =>
                              updateContact(event, contactIndex, "email")
                            }
                            type="email"
                          />
                        </div>
                        <div className="form-group">
                          <Input
                            label="Phone number"
                            value={contact.phone ? contact.phone : ""}
                            name="contact-phone"
                            onChange={(event: ChangeEvent<HTMLInputElement>) =>
                              updateContact(event, contactIndex, "phone")
                            }
                            type="tel"
                          />
                        </div>
                      </React.Fragment>
                    )
                  )
                ) : (
                  <div className="form-group">
                    <h4 className="form-group__title">Additional contact</h4>
                  </div>
                )}
                <Button variant="ghost" onClick={addContact}>
                  <>
                    <IconPlusSmall className="button__icon" />
                    Add additional contact
                  </>
                </Button>
              </TabPanel>

              {/* Address */}
              <TabPanel>
                <div className="detail__top">
                  <h3>Address</h3>
                </div>

                <div className="form-group">
                  <Input
                    value={supplier.car?.CompanyAddress?.GENADR1?.[0] || ""}
                    name="car.CompanyAddress.GENADR1"
                    label="Address"
                    placeholder="e.g. 123, ABC Street"
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      if (!e.target.value) {
                        return;
                      }

                      setSupplier({
                        ...supplier,
                        car: {
                          ...supplier.car,
                          CompanyAddress: {
                            ...supplier.car.CompanyAddress,
                            GENADR1: [e.target.value],
                          },
                        },
                      });
                    }}
                  />
                </div>

                <div className="form-group">
                  <Input
                    value={supplier.car?.CompanyAddress?.GENCITY?.[0] || ""}
                    name="car.CompanyAddress.GENCITY"
                    label="City"
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      if (!e.target.value) {
                        return;
                      }

                      setSupplier({
                        ...supplier,
                        car: {
                          ...supplier.car,
                          CompanyAddress: {
                            ...supplier.car.CompanyAddress,
                            GENCITY: [e.target.value],
                          },
                        },
                      });
                    }}
                  />
                </div>

                <div className="form-group">
                  <Input
                    value={supplier.car?.CompanyAddress?.GENSTATE?.[0] || ""}
                    name="car.CompanyAddress.GENSTATE"
                    label="State"
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      if (!e.target.value) {
                        return;
                      }

                      setSupplier({
                        ...supplier,
                        car: {
                          ...supplier.car,
                          CompanyAddress: {
                            ...supplier.car.CompanyAddress,
                            GENSTATE: [e.target.value],
                          },
                        },
                      });
                    }}
                  />
                </div>

                <div className="form-group">
                  <Input
                    value={supplier.car?.CompanyAddress?.GENPOST?.[0] || ""}
                    name="car.CompanyAddress.GENPOST"
                    label="Postal code"
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      if (!e.target.value) {
                        return;
                      }

                      setSupplier({
                        ...supplier,
                        car: {
                          ...supplier.car,
                          CompanyAddress: {
                            ...supplier.car.CompanyAddress,
                            GENPOST: [e.target.value],
                          },
                        },
                      });
                    }}
                  />
                </div>

                <div className="form-group">
                  <div style={{ zIndex: 11, position: "relative" }}>
                    <MapsAutocomplete
                      label="Country"
                      placeholder="Add new country"
                      value={null}
                      name="car.CompanyAddress.GENLAND"
                      onSelectItem={(item: MapLocation) => {
                        if (!item) {
                          return;
                        }

                        if (!Object.keys(countries).includes(item.country)) {
                          alert(
                            `ISO code mapping for country "${item.country}" not defined. Please contact IT support.`
                          );
                          return;
                        }

                        setSupplier({
                          ...supplier,
                          car: {
                            ...supplier.car,
                            CompanyAddress: {
                              ...supplier.car.CompanyAddress,
                              GENLAND: [countries[item.country]],
                            },
                          },
                        });
                      }}
                    />
                  </div>
                  {supplier.car?.CompanyAddress?.GENLAND?.[0] ? (
                    <div style={{ marginTop: 6 }}>
                      Current country:{" "}
                      {supplier.car?.CompanyAddress?.GENLAND?.[0]}
                    </div>
                  ) : null}
                </div>
              </TabPanel>

              {/* Country coverage */}
              <TabPanel>
                <div className="detail__top">
                  <h3>Country coverage</h3>
                </div>

                {context.currentUser.custom?.admin && (
                  <div className="form-group">
                    <Checkbox
                      value="on"
                      switch
                      label={
                        supplierCarCountry
                          ? `Origin or destination should match supplier's location: ${context.countryCodes[supplierCarCountry].name} (${supplierCarCountry})`
                          : undefined
                      }
                      name="ignoreSupplierLocation"
                      checked={
                        supplier.ignoreSupplierLocation !== undefined
                          ? !supplier.ignoreSupplierLocation
                          : true
                      }
                      weight="bold"
                      onChange={() =>
                        setSupplier({
                          ...supplier,
                          ignoreSupplierLocation:
                            supplier.ignoreSupplierLocation !== undefined
                              ? !supplier.ignoreSupplierLocation
                              : false,
                        })
                      }
                    />
                    {supplierCarCountry ? (
                      <p>
                        When enabled this supplier will be displayed as a
                        suggestion for a rate request when "
                        {context.countryCodes[supplierCarCountry].name}" is an
                        origin or destination country.
                      </p>
                    ) : null}
                  </div>
                )}

                {subRegionOptions[supplierCarCountry as CountryCode] && (
                  <Select
                    label="Location sub-region"
                    value={supplier.locationSubRegions}
                    name="locationSubRegion"
                    multi={true}
                    placeholder="Select country sub regions where supplier operates"
                    onChange={(option: StrictDict) =>
                      setSupplier({
                        ...supplier,
                        locationSubRegions: option,
                      })
                    }
                    options={subRegionOptions[supplierCarCountry]}
                  />
                )}

                <hr />

                <h4>Coverage</h4>
                <div className="card__row-spacer"></div>

                <div className="form-group">
                  <Checkbox
                    value="on"
                    label="Supplier operates globally"
                    name="operatesGlobally"
                    checked={isGlobal}
                    onChange={handleGlobalToggle}
                  />
                </div>

                <div style={{ zIndex: 11, position: "relative" }}>
                  <MapsAutocomplete
                    placeholder="Add new country"
                    value={null}
                    name="search"
                    onSelectItem={onCoverageAdd}
                  />
                </div>
                <ul className="country-list">
                  {loadingGlobal
                    ? Array.from({ length: 5 }).map((_, i) => (
                        <li className="country-list__item country-list__item--skeleton" key={i}>
                          <span className="country-list__item-title country-list__skeleton-block country-list__skeleton-block--name" />
                          <span className="country-list__item-continent country-list__skeleton-block country-list__skeleton-block--continent" />
                        </li>
                      ))
                    : supplier.coverage?.map(
                    (coverageItem: any, index: number) => (
                      <li className="country-list__item" key={index}>
                        <span className="country-list__item-title">
                          {coverageItem.item.country}
                        </span>
                        <span className="country-list__item-continent">
                          {coverageItem.item.continent}
                        </span>
                        <Button
                          variant="ghost"
                          tip="Delete"
                          size="small"
                          circle
                          onClick={() => removeCountry(index)}
                        >
                          <IconCrossRed />
                        </Button>
                      </li>
                    )
                  )}
                </ul>


                <hr />

                <h4>Add specific lanes</h4>
                <div className="card__row-spacer"></div>
                <form onSubmit={addNewLane}>
                  <table className="column-table">
                    <thead>
                      <tr>
                        <th>
                          <label>Origin</label>
                        </th>
                        <th colSpan={2}>
                          <label>Destination</label>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>
                          <MapsAutocomplete
                            placeholder="Add origin"
                            required
                            value={newLane.origin}
                            name="search"
                            onSelectItem={(location: MapLocation) =>
                              updateNewLane(location, "origin")
                            }
                          />
                        </td>
                        <td>
                          <MapsAutocomplete
                            placeholder="Add destination"
                            required
                            value={newLane.destination}
                            name="search"
                            onSelectItem={(location: MapLocation) =>
                              updateNewLane(location, "destination")
                            }
                          />
                        </td>
                        <td width={0}>
                          <Button variant="primary" type="submit">
                            Add
                          </Button>
                        </td>
                      </tr>
                      {supplier.coverageLanes?.map(
                        (coverageItem: any, index: number) => (
                          <tr className="column-table__list-item" key={index}>
                            <td>
                              <span className="country-list__item-title">
                                {coverageItem.lane.origin.country}
                              </span>
                              <span className="country-list__item-continent">
                                {coverageItem.lane.origin.continent}
                              </span>
                            </td>
                            <td>
                              <span className="country-list__item-title">
                                {coverageItem.lane.destination.country}
                              </span>
                              <span className="country-list__item-continent">
                                {coverageItem.lane.destination.continent}
                              </span>
                            </td>
                            <td>
                              <Button
                                variant="ghost"
                                tip="Delete"
                                size="small"
                                circle
                                onClick={() => removeLane(index)}
                              >
                                <IconCrossRed />
                              </Button>
                            </td>
                          </tr>
                        )
                      )}
                    </tbody>
                  </table>
                </form>
              </TabPanel>
            </div>
          </Tabs>
        </div>
      </div>
    </>
  );
}

const countries: Record<string, string> = {
  Afghanistan: "AF",
  Albania: "AL",
  Algeria: "DZ",
  Andorra: "AD",
  Angola: "AO",
  "Antigua and Barbuda": "AG",
  Argentina: "AR",
  Armenia: "AM",
  Australia: "AU",
  Austria: "AT",
  Azerbaijan: "AZ",
  Bahamas: "BS",
  Bahrain: "BH",
  Bangladesh: "BD",
  Barbados: "BB",
  Belarus: "BY",
  Belgium: "BE",
  Belize: "BZ",
  Benin: "BJ",
  Bhutan: "BT",
  Bolivia: "BO",
  "Bosnia and Herzegovina": "BA",
  Botswana: "BW",
  Brazil: "BR",
  Brunei: "BN",
  Bulgaria: "BG",
  "Burkina Faso": "BF",
  Burundi: "BI",
  "Cabo Verde": "CV",
  Cambodia: "KH",
  Cameroon: "CM",
  Canada: "CA",
  "Central African Republic": "CF",
  Chad: "TD",
  Chile: "CL",
  China: "CN",
  Colombia: "CO",
  Comoros: "KM",
  "Congo (Republic of the)": "CG",
  "Congo (Democratic Republic of the)": "CD",
  "Costa Rica": "CR",
  Croatia: "HR",
  Cuba: "CU",
  Cyprus: "CY",
  "Czech Republic": "CZ",
  Denmark: "DK",
  Djibouti: "DJ",
  Dominica: "DM",
  "Dominican Republic": "DO",
  Ecuador: "EC",
  Egypt: "EG",
  "El Salvador": "SV",
  "Equatorial Guinea": "GQ",
  Eritrea: "ER",
  Estonia: "EE",
  Eswatini: "SZ",
  Ethiopia: "ET",
  Fiji: "FJ",
  Finland: "FI",
  France: "FR",
  Gabon: "GA",
  Gambia: "GM",
  Georgia: "GE",
  Germany: "DE",
  Ghana: "GH",
  Greece: "GR",
  Grenada: "GD",
  Guatemala: "GT",
  Guinea: "GN",
  "Guinea-Bissau": "GW",
  Guyana: "GY",
  Haiti: "HT",
  Honduras: "HN",
  Hungary: "HU",
  Iceland: "IS",
  India: "IN",
  Indonesia: "ID",
  Iran: "IR",
  Iraq: "IQ",
  Ireland: "IE",
  Israel: "IL",
  Italy: "IT",
  Jamaica: "JM",
  Japan: "JP",
  Jordan: "JO",
  Kazakhstan: "KZ",
  Kenya: "KE",
  Kiribati: "KI",
  "Korea (North)": "KP",
  "Korea (South)": "KR",
  Kuwait: "KW",
  Kyrgyzstan: "KG",
  Laos: "LA",
  Latvia: "LV",
  Lebanon: "LB",
  Lesotho: "LS",
  Liberia: "LR",
  Libya: "LY",
  Liechtenstein: "LI",
  Lithuania: "LT",
  Luxembourg: "LU",
  Madagascar: "MG",
  Malawi: "MW",
  Malaysia: "MY",
  Maldives: "MV",
  Mali: "ML",
  Malta: "MT",
  "Marshall Islands": "MH",
  Mauritania: "MR",
  Mauritius: "MU",
  Mexico: "MX",
  Micronesia: "FM",
  Moldova: "MD",
  Monaco: "MC",
  Mongolia: "MN",
  Montenegro: "ME",
  Morocco: "MA",
  Mozambique: "MZ",
  Myanmar: "MM",
  Namibia: "NA",
  Nauru: "NR",
  Nepal: "NP",
  Netherlands: "NL",
  "New Zealand": "NZ",
  Nicaragua: "NI",
  Niger: "NE",
  Nigeria: "NG",
  "North Macedonia": "MK",
  Norway: "NO",
  Oman: "OM",
  Pakistan: "PK",
  Palau: "PW",
  Panama: "PA",
  "Papua New Guinea": "PG",
  Paraguay: "PY",
  Peru: "PE",
  Philippines: "PH",
  Poland: "PL",
  Portugal: "PT",
  Qatar: "QA",
  Romania: "RO",
  Russia: "RU",
  Rwanda: "RW",
  "Saint Kitts and Nevis": "KN",
  "Saint Lucia": "LC",
  "Saint Vincent and the Grenadines": "VC",
  Samoa: "WS",
  "San Marino": "SM",
  "Sao Tome and Principe": "ST",
  "Saudi Arabia": "SA",
  Senegal: "SN",
  Serbia: "RS",
  Seychelles: "SC",
  "Sierra Leone": "SL",
  Singapore: "SG",
  Slovakia: "SK",
  Slovenia: "SI",
  "Solomon Islands": "SB",
  Somalia: "SO",
  "South Africa": "ZA",
  "South Sudan": "SS",
  Spain: "ES",
  "Sri Lanka": "LK",
  Sudan: "SD",
  Suriname: "SR",
  Sweden: "SE",
  Switzerland: "CH",
  Syria: "SY",
  Taiwan: "TW",
  Tajikistan: "TJ",
  Tanzania: "TZ",
  Thailand: "TH",
  "Timor-Leste": "TL",
  Togo: "TG",
  Tonga: "TO",
  "Trinidad and Tobago": "TT",
  Tunisia: "TN",
  Turkey: "TR",
  Turkmenistan: "TM",
  Tuvalu: "TV",
  Uganda: "UG",
  Ukraine: "UA",
  "United Arab Emirates": "AE",
  "United Kingdom": "GB",
  "United States": "US",
  Uruguay: "UY",
  Uzbekistan: "UZ",
  Vanuatu: "VU",
  "Vatican City": "VA",
  Venezuela: "VE",
  Vietnam: "VN",
  Yemen: "YE",
  Zambia: "ZM",
  Zimbabwe: "ZW",
};
